import type {
  ScenarioIssue,
  ScenarioIssueResolution,
  ScenarioIssueSeverity,
  ScenarioIssueType,
} from "@/types/scenarios";
import { graphqlRequest } from "../graphqlClient";
import type {
  ScenarioAccessViewConnection,
  ScenarioAccessViewFilterInput,
} from "../models/scenarioAccessView";
import type {
  ScenarioApprovalViewConnection,
  ScenarioApprovalViewFilterInput,
  ScenarioApprovalViewNode,
} from "../models/scenarioApprovalView";
import type {
  ScenarioAuditLogEntry,
  ScenarioAuditLogViewConnection,
  ScenarioAuditLogViewFilterInput,
  ScenarioAuditLogViewNode,
} from "../models/scenarioAuditLogView";
import type {
  ScenarioIssueViewConnection,
  ScenarioIssueViewFilterInput,
  ScenarioIssueViewNode,
} from "../models/scenarioIssueView";
import type {
  ScenarioVersionViewConnection,
  ScenarioVersionViewFilterInput,
  ScenarioVersionViewNode,
} from "../models/scenarioVersionView";
import type {
  ScenarioViewConnection,
  ScenarioViewFilterInput,
} from "../models/scenarioView";

export interface ScenarioViewSortInput {
  field: string;
  order: "Ascending" | "Descending";
}

interface ScenarioViewListResponse {
  scenarios: {
    scenarioViewList: ScenarioViewConnection;
  };
}

const SCENARIO_VIEW_NODE_FIELDS = `
  id
  name
  description
  status
  priority
  label
  category
  createdByUserId
  createdByUserName
  modifiedByUserId
  currentVersion
  commentCount
  parkedAt
  parkedBy
  parkedByName
  parkedReason
  reviewDate
  approvalWorkflowId
  currentStageId
  currentStageOrder
  currentStageName
  minCash
  coverage
  netCashImpact
  adjustmentCount
  refreshStatus
  freshness
  canView
  canComment
  canEdit
  isOwner
  isShared
  isInherited
  isFavorite
  dateCreated
  dateModified
`;

export async function getScenariosGraphql(
  filter?: ScenarioViewFilterInput,
  first?: number,
  after?: string,
  sort?: ScenarioViewSortInput[],
): Promise<ScenarioViewConnection> {
  const query = `
    query ScenarioViewList($filter: ScenarioViewFilterInput, $first: Int, $after: String, $sort: [ScenarioViewSortSortByInput!]) {
      scenarios {
        scenarioViewList(filter: $filter, first: $first, after: $after, sort: $sort) {
          edges {
            cursor
            node {${SCENARIO_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioViewListResponse>(query, {
    filter,
    first,
    after,
    sort,
  });
  return data.scenarios.scenarioViewList;
}

interface ScenarioViewCountResponse {
  scenarios: {
    scenarioViewCount: number;
  };
}

export async function getScenariosCount(
  filter?: ScenarioViewFilterInput,
): Promise<number> {
  const query = `
    query ScenarioViewCount($filter: ScenarioViewFilterInput) {
      scenarios {
        scenarioViewCount(filter: $filter)
      }
    }
  `;
  const data = await graphqlRequest<ScenarioViewCountResponse>(query, {
    filter,
  });
  return data.scenarios.scenarioViewCount;
}

const SCENARIO_ACCESS_VIEW_NODE_FIELDS = `
  scenarioId
  userId
  userName
  isOwner
  canView
  canComment
  canEdit
  isExplicitShare
  isInherited
`;

interface ScenarioAccessViewListResponse {
  scenarios: {
    scenarioAccessViewList: ScenarioAccessViewConnection;
  };
}

/**
 * Fetch every user who can access a scenario (owner, explicit shares and users
 * inheriting status-conditional access), sourced from vw_ScenarioAccess. The
 * server gates this by scenario access, so a caller without access receives an
 * empty connection.
 */
export async function getScenarioAccessGraphql(
  scenarioId: number,
  first = 500,
): Promise<ScenarioAccessViewConnection> {
  const filter: ScenarioAccessViewFilterInput = {
    scenarioId: { eq: scenarioId },
  };
  const query = `
    query ScenarioAccessViewList($filter: ScenarioAccessViewFilterInput, $first: Int) {
      scenarios {
        scenarioAccessViewList(filter: $filter, first: $first) {
          edges {
            cursor
            node {${SCENARIO_ACCESS_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioAccessViewListResponse>(query, {
    filter,
    first,
  });
  return data.scenarios.scenarioAccessViewList;
}

/**
 * Count of scenarios the current user can see whose derived freshness is "Updating".
 * Drives the scenario refresh banner — polled only while the count is > 0 and only on
 * the Scenarios / Scenario detail pages.
 */
export async function getUpdatingScenariosCount(): Promise<number> {
  return getScenariosCount({ freshness: { eq: "Updating" } });
}

const SCENARIO_ISSUE_VIEW_NODE_FIELDS = `
  id
  scenarioId
  type
  severity
  title
  description
  relatedAdjustmentId
  relatedChangeName
  impactAmount
  impactDescription
  detectedAt
  resolvedAt
  resolvedByUserId
  resolution
`;

interface ScenarioIssueViewListResponse {
  scenarios: {
    scenarioIssueViewList: ScenarioIssueViewConnection;
  };
}

/**
 * Map a GraphQL scenario issue node (from vw_ScenarioIssues) into the frontend
 * `ScenarioIssue` shape used by the Issues tab. Date strings are converted to
 * `Date`, and the backend `relatedAdjustmentId` becomes the frontend
 * `relatedChangeId`.
 */
export function mapScenarioIssueNode(
  node: ScenarioIssueViewNode,
): ScenarioIssue {
  return {
    id: String(node.id),
    type: node.type as ScenarioIssueType,
    severity: node.severity as ScenarioIssueSeverity,
    title: node.title,
    description: node.description ?? "",
    detectedAt: new Date(node.detectedAt),
    relatedChangeId:
      node.relatedAdjustmentId != null
        ? String(node.relatedAdjustmentId)
        : undefined,
    relatedChangeName: node.relatedChangeName ?? undefined,
    impactAmount: node.impactAmount ?? undefined,
    impactDescription: node.impactDescription ?? undefined,
    resolvedAt: node.resolvedAt ? new Date(node.resolvedAt) : undefined,
    resolvedBy: node.resolvedByUserId ?? undefined,
    resolution:
      (node.resolution as ScenarioIssueResolution | null) ?? undefined,
  };
}

/**
 * Fetch the issues detected for a scenario (sourced from vw_ScenarioIssues) and
 * map them into the frontend `ScenarioIssue` type.
 */
export async function getScenarioIssuesGraphql(
  scenarioId: number,
  first = 200,
): Promise<ScenarioIssue[]> {
  const filter: ScenarioIssueViewFilterInput = {
    scenarioId: { eq: scenarioId },
  };
  const query = `
    query ScenarioIssueViewList($filter: ScenarioIssueViewFilterInput, $first: Int) {
      scenarios {
        scenarioIssueViewList(filter: $filter, first: $first) {
          edges {
            cursor
            node {${SCENARIO_ISSUE_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioIssueViewListResponse>(query, {
    filter,
    first,
  });
  return data.scenarios.scenarioIssueViewList.edges.map((edge) =>
    mapScenarioIssueNode(edge.node),
  );
}

interface ScenarioIssueViewCountResponse {
  scenarios: {
    scenarioIssueViewCount: number;
  };
}

/**
 * Count of unresolved issues for a scenario (ResolvedAt is null). Drives the
 * Issues tab badge and the overlay picker alert in live mode.
 */
export async function getScenarioIssuesCount(
  scenarioId: number,
): Promise<number> {
  const filter: ScenarioIssueViewFilterInput = {
    scenarioId: { eq: scenarioId },
    resolvedAt: { isBlank: true },
  };
  const query = `
    query ScenarioIssueViewCount($filter: ScenarioIssueViewFilterInput) {
      scenarios {
        scenarioIssueViewCount(filter: $filter)
      }
    }
  `;
  const data = await graphqlRequest<ScenarioIssueViewCountResponse>(query, {
    filter,
  });
  return data.scenarios.scenarioIssueViewCount;
}

const SCENARIO_AUDIT_LOG_VIEW_NODE_FIELDS = `
  id
  scenarioId
  auditType
  title
  description
  auditDateTimeUTC
  auditUserId
  userName
  userFirstName
  userLastName
  userEmail
  oldValue
  newValue
`;

interface ScenarioAuditLogViewListResponse {
  scenarios: {
    scenarioAuditLogViewList: ScenarioAuditLogViewConnection;
  };
}

/** Parse an OldValue/NewValue JSON payload; malformed JSON is dropped rather than thrown. */
function parseAuditValues(
  raw: string | null,
): Record<string, unknown> | undefined {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Map a GraphQL audit node (from vw_ScenarioAuditLog) into the shape the Audit
 * Log tab renders. The acting user falls back name → email → user id, and stays
 * undefined for system events (null AuditUserId).
 */
export function mapScenarioAuditLogNode(
  node: ScenarioAuditLogViewNode,
): ScenarioAuditLogEntry {
  return {
    id: String(node.id),
    auditType: node.auditType,
    title: node.title,
    description: node.description ?? undefined,
    timestamp: new Date(node.auditDateTimeUTC),
    user: node.userName ?? node.userEmail ?? node.auditUserId ?? undefined,
    oldValues: parseAuditValues(node.oldValue),
    newValues: parseAuditValues(node.newValue),
  };
}

/**
 * Fetch a scenario's audit log (sourced from vw_ScenarioAuditLog), most recent
 * first. This view is the only read path for the audit log — there is no REST
 * endpoint.
 */
export async function getScenarioAuditLogGraphql(
  scenarioId: number,
  first = 200,
): Promise<ScenarioAuditLogEntry[]> {
  const filter: ScenarioAuditLogViewFilterInput = {
    scenarioId: { eq: scenarioId },
  };
  const sort: ScenarioViewSortInput[] = [
    { field: "AuditDateTimeUTC", order: "Descending" },
  ];
  const query = `
    query ScenarioAuditLogViewList($filter: ScenarioAuditLogViewFilterInput, $first: Int, $sort: [ScenarioAuditLogViewSortSortByInput!]) {
      scenarios {
        scenarioAuditLogViewList(filter: $filter, first: $first, sort: $sort) {
          edges {
            cursor
            node {${SCENARIO_AUDIT_LOG_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioAuditLogViewListResponse>(query, {
    filter,
    first,
    sort,
  });
  return data.scenarios.scenarioAuditLogViewList.edges
    .map((edge) => mapScenarioAuditLogNode(edge.node))
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

interface ScenarioApprovalViewListResponse {
  scenarios: {
    scenarioApprovalViewList: ScenarioApprovalViewConnection;
  };
}

const SCENARIO_APPROVAL_VIEW_NODE_FIELDS = `
  id
  scenarioId
  approvalWorkflowId
  approvalWorkflowStageId
  stageName
  stageOrder
  userId
  userName
  decision
  comments
  dateCreated
`;

/**
 * Approval decisions recorded against a scenario (vw_ScenarioApprovals), oldest first.
 * Read path for the Approval Status modal — approve/reject still POST via REST.
 */
export async function getScenarioApprovalsGraphql(
  scenarioId: number,
  first = 200,
): Promise<ScenarioApprovalViewNode[]> {
  const filter: ScenarioApprovalViewFilterInput = {
    scenarioId: { eq: scenarioId },
  };
  const query = `
    query ScenarioApprovalViewList($filter: ScenarioApprovalViewFilterInput, $first: Int) {
      scenarios {
        scenarioApprovalViewList(filter: $filter, first: $first) {
          edges {
            cursor
            node {${SCENARIO_APPROVAL_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioApprovalViewListResponse>(query, {
    filter,
    first,
  });
  return data.scenarios.scenarioApprovalViewList.edges.map((edge) => edge.node);
}

interface ScenarioVersionViewListResponse {
  scenarios: {
    scenarioVersionViewList: ScenarioVersionViewConnection;
  };
}

const SCENARIO_VERSION_VIEW_NODE_FIELDS = `
  id
  scenarioId
  versionNumber
  description
  scenarioSnapshot
  changesSnapshot
  createdByUserId
  createdByUserName
  dateCreated
`;

/**
 * Saved version snapshots of a scenario (vw_ScenarioVersions), newest first.
 * Read path for the History tab — creating and restoring versions still POST
 * via REST.
 */
export async function getScenarioVersionsGraphql(
  scenarioId: number,
  first = 200,
  after?: string,
  sort: ScenarioViewSortInput[] = [
    { field: "VersionNumber", order: "Descending" },
  ],
): Promise<ScenarioVersionViewNode[]> {
  const filter: ScenarioVersionViewFilterInput = {
    scenarioId: { eq: scenarioId },
  };
  const query = `
    query ScenarioVersionViewList($filter: ScenarioVersionViewFilterInput, $first: Int, $after: String, $sort: [ScenarioVersionViewSortSortByInput!]) {
      scenarios {
        scenarioVersionViewList(filter: $filter, first: $first, after: $after, sort: $sort) {
          edges {
            cursor
            node {${SCENARIO_VERSION_VIEW_NODE_FIELDS}}
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<ScenarioVersionViewListResponse>(query, {
    filter,
    first,
    after,
    sort,
  });
  return data.scenarios.scenarioVersionViewList.edges.map((edge) => edge.node);
}
